<app-side-menu>
  <div class="profile-create-container">
    <div class="card">
      <div class="card-header">
        <h3>
          <i class="fas fa-user-shield me-2"></i>
          {{ profileTitle }}
        </h3>
      </div>

      <div class="card-body" [class.loading-overlay]="loading">
        <app-loading [show]="loading" [message]="isEditMode ? 'Atualizando perfil...' : 'Criando perfil...'"></app-loading>
        
        <div *ngIf="!loading">
        <form [formGroup]="profileForm" (ngSubmit)="onSubmit()">
          
          <!-- Informações Básicas -->
          <div class="form-section">
            <h5 class="section-title">
              <i class="fas fa-info-circle me-2"></i>
              Informações Básicas
            </h5>

            <div class="row">
              <!-- Nome do Perfil -->
              <div class="col-md-12 mb-3">
                <label for="name" class="form-label required">Nome do Perfil</label>
                <input
                  type="text"
                  class="form-control"
                  id="name"
                  formControlName="name"
                  placeholder="Digite o nome do perfil"
                  [class.is-invalid]="hasError('name', 'required') || hasError('name', 'minlength') || hasError('name', 'maxlength')">
                <div class="invalid-feedback" *ngIf="profileForm.get('name')?.invalid && (profileForm.get('name')?.dirty || profileForm.get('name')?.touched)">
                  {{ getErrorMessage('name') }}
                </div>
                <small class="form-text text-muted">
                  Mínimo 3 caracteres, máximo 100 caracteres
                </small>
              </div>
            </div>

            <!-- Descrição -->
            <div class="row">
              <div class="col-12 mb-3">
                <label for="description" class="form-label">Descrição</label>
                <textarea
                  class="form-control"
                  id="description"
                  formControlName="description"
                  rows="3"
                  placeholder="Digite uma descrição para o perfil (opcional)"
                  [class.is-invalid]="hasError('description', 'maxlength')"></textarea>
                <div class="invalid-feedback" *ngIf="profileForm.get('description')?.invalid && (profileForm.get('description')?.dirty || profileForm.get('description')?.touched)">
                  {{ getErrorMessage('description') }}
                </div>
                <small class="form-text text-muted">
                  Máximo 500 caracteres
                </small>
              </div>
            </div>
          </div>

          <!-- Grupos Existentes -->
          <div class="form-section">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="section-title mb-0">
                <i class="fas fa-users me-2"></i>
                Grupos Existentes
              </h5>
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="includeGroups"
                  formControlName="includeGroups">
                <label class="form-check-label" for="includeGroups">
                  Associar grupos existentes
                </label>
              </div>
            </div>

            <div *ngIf="showGroupsSection" class="groups-section">
              <div class="alert alert-info mb-3">
                <i class="fas fa-info-circle me-2"></i>
                Selecione os grupos que deseja associar a este perfil.
              </div>

              <div *ngIf="loadingGroups" class="text-center py-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Carregando grupos...</span>
                </div>
              </div>

              <div *ngIf="!loadingGroups && groups.length === 0" class="text-center py-4">
                <p class="text-muted">
                  <i class="fas fa-exclamation-circle me-2"></i>
                  Nenhum grupo disponível
                </p>
              </div>

              <div *ngIf="!loadingGroups && groups.length > 0" class="groups-list">
                <div class="row">
                  <div class="col-md-6 mb-2" *ngFor="let group of groups; trackBy: trackByGroupId">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        [id]="'group-' + group.id"
                        [checked]="isGroupSelected(group.id)"
                        (change)="onGroupCheckboxChange($event, group.id)">
                      <label class="form-check-label" [for]="'group-' + group.id">
                        <div class="d-flex align-items-center justify-content-between">
                          <div>
                            <strong>{{ group.name }}</strong>
                            <span *ngIf="isGroupOriginallyAssociated(group.id)" class="badge bg-success ms-2" title="Já associado a este perfil">
                              <i class="fas fa-check me-1"></i>Associado
                            </span>
                            <span *ngIf="!isGroupOriginallyAssociated(group.id) && isGroupSelected(group.id)" class="badge bg-info ms-2" title="Será associado ao salvar">
                              <i class="fas fa-plus me-1"></i>Novo
                            </span>
                            <small class="text-muted d-block" *ngIf="group.description">{{ group.description }}</small>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Overrides de Permissões (Permissões Específicas) -->
          <div class="form-section">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="section-title mb-0">
                <i class="fas fa-key me-2"></i>
                Permissões do Perfil
              </h5>
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="includeOverrides"
                  formControlName="includeOverrides">
                <label class="form-check-label" for="includeOverrides">
                  Configurar permissões específicas
                </label>
              </div>
            </div>

            <div *ngIf="showOverridesSection" class="overrides-section">
              <div class="alert alert-info mb-3">
                <i class="fas fa-info-circle me-2"></i>
                Selecione as permissões que deseja <strong>Permitir</strong> ou <strong>Bloquear</strong> para este perfil.
                Permissões não selecionadas serão bloqueadas por padrão.
              </div>

              <!-- Aba minimizada: carrega permissões ao clicar -->
              <div class="permission-group-card mb-3">
                <div class="permission-group-header" (click)="toggleOverridesExpanded()">
                  <div class="d-flex align-items-center">
                    <i class="fas me-2" [ngClass]="overridesExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    <strong>Permissões</strong>
                    <span class="badge bg-secondary ms-2" *ngIf="permissionOverridesMap.size > 0">
                      {{ permissionOverridesMap.size }} selecionada(s)
                    </span>
                  </div>
                  <small class="text-muted" *ngIf="!overridesExpanded">Clique para carregar</small>
                </div>

                <div *ngIf="overridesExpanded" class="permission-group-body p-3">

                  <div *ngIf="loadingPermissions" class="text-center py-3">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Carregando permissões...</span>
                    </div>
                  </div>

                  <div *ngIf="!loadingPermissions && getPermissionGroupIds().length === 0" class="text-center py-4">
                    <p class="text-muted">
                      <i class="fas fa-exclamation-circle me-2"></i>
                      Nenhuma permissão disponível no sistema.
                    </p>
                  </div>

                  <!-- Permissões agrupadas por módulo -->
                  <div *ngIf="!loadingPermissions && getPermissionGroupIds().length > 0">

                    <!-- Ações globais -->
                    <div class="d-flex gap-2 mb-3">
                      <button type="button" class="btn btn-outline-success btn-sm" (click)="allowAllPermissions()">
                        <i class="fas fa-check-double me-1"></i> Permitir Todas
                      </button>
                      <button type="button" class="btn btn-outline-danger btn-sm" (click)="denyAllPermissions()">
                        <i class="fas fa-ban me-1"></i> Bloquear Todas
                      </button>
                    </div>

                    <!-- Campo de pesquisa de permissões -->
                    <div class="mb-3">
                      <div class="input-group">
                        <span class="input-group-text">
                          <i class="fas fa-search"></i>
                        </span>
                        <input
                          type="text"
                          class="form-control"
                          placeholder="Pesquisar permissão por nome ou descrição..."
                          [(ngModel)]="permissionSearchTerm"
                          [ngModelOptions]="{ standalone: true }">
                        <button
                          *ngIf="permissionSearchTerm"
                          type="button"
                          class="btn btn-outline-secondary"
                          (click)="permissionSearchTerm = ''">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>

                    <!-- Card por grupo/módulo -->
                    <div *ngFor="let groupId of getPermissionGroupIds()" class="permission-group-card mb-3">
                      <div class="permission-group-header" (click)="toggleGroupCollapse(groupId)">
                        <div class="d-flex align-items-center">
                          <i class="fas me-2" [ngClass]="isGroupCollapsed(groupId) ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
                               <span class="badge bg-secondary ms-2">{{ getPermissionsByGroupId(groupId).length }}</span>
                          <span class="badge bg-success ms-1" *ngIf="countAllowedInGroup(groupId) > 0">
                            {{ countAllowedInGroup(groupId) }} permitida(s)
                          </span>
                          <span class="badge bg-danger ms-1" *ngIf="countDeniedInGroup(groupId) > 0">
                            {{ countDeniedInGroup(groupId) }} bloqueada(s)
                          </span>
                        </div>
                      </div>

                      <div *ngIf="!isGroupCollapsed(groupId)" class="permission-group-body">
                        <table class="table table-sm table-hover mb-0">
                          <thead>
                            <tr>
                              <th style="width: 40%">Permissão</th>
                              <th style="width: 30%">Descrição</th>
                              <th style="width: 30%" class="text-center">Ação</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr *ngFor="let perm of getPermissionsByGroupId(groupId)" [ngClass]="{
                              'table-success-light': getPermissionOverrideType(perm.id) === 'Allow',
                              'table-danger-light': getPermissionOverrideType(perm.id) === 'Deny'
                            }">
                              <td>
                                <code class="permission-name">{{ perm.name }}</code>
                              </td>
                              <td>
                                <small class="text-muted">{{ perm.description || '—' }}</small>
                              </td>
                              <td class="text-center">
                                <div class="btn-group btn-group-sm" role="group">
                                  <button type="button"
                                    class="btn"
                                    [ngClass]="getPermissionOverrideType(perm.id) === 'Allow' ? 'btn-success' : 'btn-outline-success'"
                                    (click)="setPermissionOverride(perm.id, 'Allow')"
                                    title="Permitir">
                                    <i class="fas fa-check me-1"></i> Permitir
                                  </button>
                                  <button type="button"
                                    class="btn"
                                    [ngClass]="getPermissionOverrideType(perm.id) === 'Deny' ? 'btn-danger' : 'btn-outline-danger'"
                                    (click)="setPermissionOverride(perm.id, 'Deny')"
                                    title="Bloquear">
                                    <i class="fas fa-ban me-1"></i> Bloquear
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Ordens de Serviço (ConfigOrders) -->
          <div class="form-section">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="section-title mb-0">
                <i class="fas fa-clipboard-list me-2"></i>
                Ordens de Serviço
              </h5>
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="includeConfigOrders"
                  formControlName="includeConfigOrders">
                <label class="form-check-label" for="includeConfigOrders">
                  Gerenciar ordens de serviço do perfil
                </label>
              </div>
            </div>

            <div *ngIf="showConfigOrdersSection" class="configorders-section">
              <div class="alert alert-info mb-3">
                <i class="fas fa-info-circle me-2"></i>
                Clique na aba abaixo para carregar as <strong>ConfigOrders</strong> e selecionar quais o perfil terá acesso.
              </div>

              <div class="permission-group-card mb-3">
                <div class="permission-group-header" (click)="toggleConfigOrdersExpanded()">
                  <div class="d-flex align-items-center">
                    <i class="fas me-2" [ngClass]="configOrdersExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    <strong>Ordens de Serviço</strong>
                    <span class="badge bg-secondary ms-2" *ngIf="selectedConfigOrders.length > 0">
                      {{ selectedConfigOrders.length }} selecionada(s)
                    </span>
                  </div>
                  <small class="text-muted" *ngIf="!configOrdersExpanded">Clique para carregar</small>
                </div>

                <div *ngIf="configOrdersExpanded" class="permission-group-body p-3">
                  <div *ngIf="loadingConfigOrders" class="text-center py-3">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Carregando ordens de serviço...</span>
                    </div>
                  </div>

                  <div *ngIf="!loadingConfigOrders && configOrders.length === 0" class="text-center py-4">
                    <p class="text-muted">
                      <i class="fas fa-exclamation-circle me-2"></i>
                      Nenhuma ConfigOrders disponível.
                    </p>
                  </div>

                  <div *ngIf="!loadingConfigOrders && configOrders.length > 0" class="groups-list">
                    <div class="row">
                      <div class="col-md-6 mb-2" *ngFor="let item of configOrders; trackBy: trackByConfigOrderId">
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            [id]="'configorder-' + item.id"
                            [checked]="isConfigOrderSelected(item.id)"
                            (change)="onConfigOrderCheckboxChange($event, item.id)">
                          <label class="form-check-label" [for]="'configorder-' + item.id">
                            <div class="d-flex align-items-center justify-content-between">
                              <div>
                                <strong>{{ item.typeOS || ('ConfigOrders #' + item.id) }}</strong>
                                <span *ngIf="isConfigOrderOriginallyAssociated(item.id)" class="badge bg-success ms-2" title="Já associado a este perfil">
                                  <i class="fas fa-check me-1"></i>Associado
                                </span>
                                <span *ngIf="!isConfigOrderOriginallyAssociated(item.id) && isConfigOrderSelected(item.id)" class="badge bg-info ms-2" title="Será associado ao salvar">
                                  <i class="fas fa-plus me-1"></i>Novo
                                </span>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Botões de Ação -->
          <div class="form-actions mt-4">
            <button
              type="button"
              class="btn btn-secondary"
              (click)="onCancel()"
              [disabled]="loading">
              <i class="fas fa-times me-2"></i>
              Cancelar
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              [disabled]="loading || profileForm.invalid">
              <i class="fas fa-save me-2"></i>
              {{ isEditMode ? 'Atualizar Perfil' : 'Criar Perfil' }}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de Sucesso -->
  <app-modal
    [isOpen]="showSuccessModal"
    [title]="successModalTitle"
    [size]="'md'"
    [closable]="true"
    (close)="closeSuccessModal()">
    <div class="text-center py-4">
      <i class="fas fa-check-circle text-success" style="font-size: 4rem;"></i>
      <p class="mt-3 mb-0" style="font-size: 1.1rem;">{{ successModalMessage }}</p>
    </div>
    <div slot="footer" class="text-center">
      <button
        type="button"
        class="btn btn-primary"
        (click)="closeSuccessModal()">
        <i class="fas fa-check me-2"></i>
        OK
      </button>
    </div>
  </app-modal>
</app-side-menu>
